import { CalendarBlankIcon, ClockIcon, CurrencyDollarIcon } from "@phosphor-icons/react";
import { handleCardCtaClick } from "../../MobileApp/webviewBridge";
import type { BookingCardData } from "./validate";
export type { BookingCardData };

export function formatBookingDate(date: string, time: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const dt = new Date(y, m - 1, d, hh, mm);

  const dateStr = dt.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const timeStr = dt.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${dateStr}, ${timeStr}`;
}

export function formatBookingTime(time: string): string {
  const [hh, mm] = time.split(":").map(Number);
  // Sentinel date — only the time portion matters.
  const dt = new Date(2000, 0, 1, hh, mm);
  return dt.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// "1 hour" / "2 hours" / "30 minutes" / "90 minutes" / "1 hour 30 minutes"
// → minutes. Returns null if the label can't be parsed.
export function parseDurationToMinutes(label: string): number | null {
  let total = 0;
  const hourMatch = label.match(/(\d+(?:\.\d+)?)\s*hour/i);
  const minMatch = label.match(/(\d+)\s*minute/i);
  if (hourMatch) total += parseFloat(hourMatch[1]) * 60;
  if (minMatch) total += parseInt(minMatch[1], 10);
  return total > 0 ? Math.round(total) : null;
}

export function formatPricePerHour(cents: number): string {
  const dollars = cents / 100;
  const fixed = Number.isInteger(dollars) ? dollars.toString() : dollars.toFixed(2);
  return `${fixed}/hr`;
}

interface BookingCardProps {
  data: BookingCardData;
}

export function BookingCard({ data }: BookingCardProps) {
  const fullLabel = formatBookingDate(data.date, data.time);
  const timeOnly = formatBookingTime(data.time);
  const minutes = parseDurationToMinutes(data.duration);
  const priceLabel = minutes
    ? formatPricePerHour(Math.round(data.subtotalCents / (minutes / 60)))
    : `${(data.subtotalCents / 100).toFixed(2)} total`;

  return (
    <div className="lk-booking-card">
      <div
        className="lk-booking-card-image"
        style={{ backgroundImage: `url(${data.facilityImage})` }}
        role="img"
        aria-label={data.facilityName}
      />
      <div className="lk-booking-card-body">
        <div className="lk-booking-card-top">
          <div className="lk-booking-card-sport">{data.sport.toUpperCase()}</div>
          <div className="lk-booking-card-name">{data.facilityName}</div>
        </div>
        <div className="lk-booking-card-rows">
          <div className="lk-booking-card-row">
            <CalendarBlankIcon size={16} weight="regular" className="lk-booking-card-icon" />
            <span>{fullLabel}</span>
          </div>
          <div className="lk-booking-card-row">
            <ClockIcon size={16} weight="regular" className="lk-booking-card-icon" />
            <span>{timeOnly}, {data.duration}</span>
          </div>
          <div className="lk-booking-card-row">
            <CurrencyDollarIcon size={16} weight="regular" className="lk-booking-card-icon" />
            <span>{priceLabel}</span>
          </div>
        </div>
        <a
          className="lk-booking-card-cta"
          href={data.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => handleCardCtaClick(e, data.bookingUrl)}
        >
          Continue to Booking
        </a>
      </div>
    </div>
  );
}
